<template>
  <span class="tw:text-13">
    <!-- state the wait instead of showing a disabled link -->
    <span v-if="remaining > 0" class="tw:text-muted">{{ $t('registration.code.resend_in', { time: formatted }) }}</span>

    <button
      v-else
      type="button"
      class="tw:font-medium tw:text-primary tw:hover:text-primary-darken tw:hover:underline tw:disabled:cursor-not-allowed tw:disabled:text-muted tw:disabled:no-underline"
      :disabled="busy"
      @click="emit('resend')">
      {{ busy ? $t('registration.code.resending') : $t('registration.code.resend') }}
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';

interface Props {
  /** cooldown in seconds, as reported by the server */
  seconds: number;
  /** a resend is in flight */
  busy?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  resend: [];
  /** the cooldown reached zero */
  finished: [];
}>();

/* ---------------------------------- STATE ---------------------------------- */
const remaining = ref(props.seconds);
let timer: ReturnType<typeof setInterval> | undefined;

/* --------------------------------- COMPUTED -------------------------------- */
const formatted = computed(() => {
  const minutes = Math.floor(remaining.value / 60);
  const seconds = remaining.value % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
});

/* --------------------------------- METHODS --------------------------------- */
function stop() {
  if (timer === undefined) return;

  clearInterval(timer);
  timer = undefined;
}

function restart(seconds = props.seconds) {
  stop();
  remaining.value = Math.max(0, Math.round(seconds));
  if (remaining.value === 0) return;

  timer = setInterval(() => {
    remaining.value -= 1;
    if (remaining.value <= 0) {
      stop();
      emit('finished');
    }
  }, 1000);
}

/* --------------------------------- WATCHERS -------------------------------- */
watch(() => props.seconds, restart, { immediate: true });

/* --------------------------------- LIFECYCLE ------------------------------- */
onBeforeUnmount(stop);

// a repeat resend often returns the same cooldown, so the watcher wouldn't fire; callers restart explicitly
defineExpose({ restart });
</script>
